import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { ContactInput } from './types';

export const contactKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactKeys.all, 'list'] as const,
  list: (params: api.ContactListParams) => [...contactKeys.lists(), params] as const,
  detail: (id: string | number) => [...contactKeys.all, 'detail', id] as const,
};

export function useContacts(params: api.ContactListParams) {
  return useQuery({
    queryKey: contactKeys.list(params),
    queryFn: () => api.listContacts(params),
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string | number) {
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => api.getContact(id),
    enabled: Boolean(id),
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContactInput) => api.createContact(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Contact created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateContact(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ContactInput>) => api.updateContact(id, input),
    onSuccess: (contact) => {
      qc.invalidateQueries({ queryKey: contactKeys.lists() });
      qc.setQueryData(contactKeys.detail(id), contact);
      toast.success('Contact updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useArchiveContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.archiveContact(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Contact archived');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.restoreContact(id),
    onSuccess: (contact) => {
      qc.invalidateQueries({ queryKey: contactKeys.lists() });
      qc.setQueryData(contactKeys.detail(contact.public_id), contact);
      toast.success('Contact restored');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function usePurgeContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.purgeContact(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Contact permanently deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
